import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

/**
 * Brand assets API client.
 *
 * Phase 1 surface — list / upload / delete tenant-scoped binary assets
 * (logos, header images). The public asset URL (`/reporting/api/assets/<id>/raw`)
 * is what authors paste into report-template parameters; this client
 * just covers the management screen.
 */

export interface Asset {
  id: string;
  tenantId: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  extension: string;
  /** Path-only URL — the host portion is implied by the SPA's API
   *  origin. Authors typically copy this verbatim into a template
   *  parameter; the renderer fetches it server-side too, so a
   *  relative path is the right shape. */
  url: string;
  createdAt: string;
  updatedAt: string;
}

const QK_LIST = ['assets', 'list'] as const;

export function useAssets(): ReturnType<typeof useQuery<Asset[]>> {
  const api = useApi();
  return useQuery<Asset[]>({
    queryKey: QK_LIST,
    queryFn: () => api.fetch<Asset[]>('/assets'),
  });
}

export interface UploadAssetInput {
  name: string;
  mimeType: string;
  /** Raw bytes — converted to base64 here before hitting the wire. */
  bytes: ArrayBuffer;
}

export function useUploadAsset(): ReturnType<typeof useMutation<Asset, Error, UploadAssetInput>> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Asset, Error, UploadAssetInput>({
    mutationFn: async (input) => {
      const base64 = arrayBufferToBase64(input.bytes);
      return api.fetch<Asset>('/assets', {
        method: 'POST',
        body: JSON.stringify({ name: input.name, mimeType: input.mimeType, base64 }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_LIST });
    },
  });
}

export function useDeleteAsset(): ReturnType<typeof useMutation<{ ok: true }, Error, string>> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => api.fetch<{ ok: true }>(`/assets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_LIST });
    },
  });
}

/** btoa() doesn't accept binary strings safely on Unicode payloads;
 *  convert through Uint8Array → fromCharCode batch → btoa. Same trick
 *  used elsewhere in the SPA for the export flow. */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

/** Detect a sensible mime type from a File's `type` and name. The
 *  browser's native sniff is good for the common cases; we only
 *  reach into the filename for SVGs reported as `text/xml`. */
export function inferMimeType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.svg')) return 'image/svg+xml';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.gif')) return 'image/gif';
  return file.type || 'application/octet-stream';
}
